import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { medicalRecordsApi, type MedicalRecord } from '@/api';

const MEDICAL_RECORD_KEY = (id: string) => ['medical-records', 'record', id] as const;

async function fetchMedicalRecord(recordId: string): Promise<MedicalRecord> {
  return medicalRecordsApi.getMedicalRecord(recordId);
}

export function useMedicalRecord(recordId: string) {
  return useQuery({
    queryKey: MEDICAL_RECORD_KEY(recordId),
    queryFn: () => fetchMedicalRecord(recordId),
    enabled: !!recordId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    retry: 2,
  });
}

export function useMedicalRecordByAppointment(appointmentId: string) {
  return useQuery({
    queryKey: ['medical-records', 'appointment', appointmentId] as const,
    queryFn: () => medicalRecordsApi.getMedicalRecordByAppointment(appointmentId),
    enabled: !!appointmentId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 10,
    retry: 2,
  });
}

export function useOpenMedicalRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recordId,
      openedBy,
      userType,
    }: {
      recordId: string;
      openedBy: string;
      userType: 'PATIENT' | 'DOCTOR' | 'ADMIN';
    }) => medicalRecordsApi.openMedicalRecord(recordId, { openedBy, userType }),
    onSuccess: (data) => {
      queryClient.setQueryData(MEDICAL_RECORD_KEY(data.id), data);
    },
  });
}

export function useSignMedicalRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recordId,
      signedBy,
      signature,
    }: {
      recordId: string;
      signedBy: string;
      signature: string;
    }) => medicalRecordsApi.signMedicalRecord(recordId, { signedBy, signature }),
    onSuccess: (data) => {
      queryClient.setQueryData(MEDICAL_RECORD_KEY(data.id), data);
    },
  });
}
